/**
 * Parses a time value string into its component parts (hour, minute, second),
 * normalizing 12-hour formats to 24-hour.
 *
 * Handles three input formats:
 * - Comma-separated (GF merge tag): "H, MM, am" or "H, MM"
 * - Colon 12-hour: "2:00pm", "2:00:30pm", "2:00 pm"
 * - Colon 24-hour: "14:00", "14:00:30"
 *
 * Seconds are optional in colon formats and default to 0 if absent.
 *
 * @param value Raw time string from a form field or hardcoded formula argument.
 * @return Parsed parts in 24-hour format, or null if the value cannot be parsed.
 */
export function parseTimeStringParts(
	value: string
): { hour: number; minute: number; second: number } | null {
	const trimmed = value.trim();

	let hour: null | number = null;
	let minute: null | number = null;
	let second = 0;
	let period: null | string = null;

	let match12 = null;
	let match24 = null;

	if (trimmed.includes(',')) {
		// Comma-separated format (default GF path):
		// "H, MM, am" if 12 hour
		// "H, MM" if 24 hour
		const parts = trimmed.split(',').map((s) => s.trim());

		// not valid, must include at minimum HH and MM
		if (parts.length < 2) {
			return null;
		}

		hour = parseInt(parts[0], 10);
		minute = parseInt(parts[1], 10);

		if (typeof parts[2] === 'string') {
			period = parts[2].toLowerCase();
		}
	} else if (
		(match12 = trimmed.match(
			/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/i
		))
	) {
		// Colon-separated 12-hour format: "2:00pm", "2:00:30pm", "2:00 pm"
		hour = parseInt(match12[1], 10);
		minute = parseInt(match12[2], 10);
		if (match12[3]) {
			second = parseInt(match12[3], 10);
		}
		period = match12[4].toLowerCase();
	} else if (
		(match24 = trimmed.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/))
	) {
		// Colon 24-hour: "14:00", "14:00:30"
		hour = parseInt(match24[1], 10);
		minute = parseInt(match24[2], 10);
		if (match24[3]) {
			second = parseInt(match24[3], 10);
		}
	}

	if (hour === null || isNaN(hour) || minute === null || isNaN(minute)) {
		return null;
	}

	if (period === 'am' && hour === 12) {
		hour = 0;
	}
	if (period === 'pm' && hour !== 12) {
		hour += 12;
	}

	return { hour, minute, second };
}
